import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import {
  ArrowForward,
  AutoAwesome,
  Check,
  East,
  Menu,
  Psychology,
  SelfImprovement,
  Spa,
} from "@mui/icons-material";
import "./App.css";

const audiences = [
  {
    number: "01",
    title: "Leadership teams",
    text: "Step out of operational pressure, improve decision quality and return aligned around what matters next.",
  },
  {
    number: "02",
    title: "Organisations",
    text: "Create a calmer, more connected culture through practical work on communication, resilience and change.",
  },
  {
    number: "03",
    title: "Private groups",
    text: "Mark a transition, reconnect with purpose or share a thoughtfully facilitated experience together.",
  },
  {
    number: "04",
    title: "Individuals",
    text: "Take dedicated space to change an unhelpful pattern, find clarity and move forward differently.",
  },
];

const pillars = [
  { title: "Regulate", text: "Settle the nervous system and reduce mental noise.", icon: <Spa /> },
  { title: "Reflect", text: "Interrupt habitual thinking with better questions.", icon: <SelfImprovement /> },
  { title: "Reframe", text: "Change the perspective and patterns holding you back.", icon: <Psychology /> },
  { title: "Realign", text: "Turn insight into clearer choices and practical action.", icon: <AutoAwesome /> },
];

const retreatFor = [
  "Clearer thinking under pressure",
  "Stronger connection and communication",
  "Support through change or transition",
  "A meaningful reset away from daily noise",
];

const outcomes = [
  "Manage stress",
  "Reduce anxiety",
  "Improve your mindset",
  "Make better decisions",
  "Gain clarity",
  "Build confidence",
  "Lead with calm",
  "Change unhelpful patterns",
  "Reconnect with purpose",
];

const talkingPoints = [
  {
    number: "01",
    title: "Regulated leadership",
    text: "Recognise fight-or-flight, understand your triggers and create enough pause to choose clarity over speed when pressure rises.",
    links: [
      { label: "Regulated leaders", url: "https://9ambusiness.co.uk/columnist/why-regulated-leaders-make-better-decisions/" },
    ],
  },
  {
    number: "02",
    title: "Sustainable performance",
    text: "Move beyond hustle, constant availability and decision fatigue towards boundaries, recovery and contribution-led leadership.",
    links: [
      { label: "The truth about hustle culture", url: "https://9ambusiness.co.uk/exclusive/the-truth-about-hustle-culture-and-how-you-can-lead-differently/" },
      { label: "A stronger Q4", url: "https://9ambusiness.co.uk/exclusive/5-ways-to-set-yourself-up-for-a-huge-q4/" },
    ],
  },
  {
    number: "03",
    title: "Goals, habits & identity",
    text: "Build change around values, identity, environment and small repeatable wins instead of relying on pressure or willpower alone.",
    links: [
      { label: "Goals that stick", url: "https://9ambusiness.co.uk/exclusive/5-most-effective-ways-for-entrepreneurs-to-stick-to-their-goals/" },
      { label: "Changing your relationship with alcohol", url: "https://wellbeingmagazine.com/how-a-mindset-shift-can-help-you-quit-drinking-by-a-leading-expert/" },
    ],
  },
  {
    number: "04",
    title: "Confidence without perfectionism",
    text: "Explore imposter thinking, self-sabotage and the need to perform perfectly, then practise a steadier form of self-belief.",
    links: [
      { label: "Imposter syndrome", url: "https://9ambusiness.co.uk/exclusive/how-does-imposter-syndrome-sabotage-careers/" },
    ],
  },
  {
    number: "05",
    title: "Decision clarity",
    text: "Use reflective tarot prompts, coaching and light hypnosis to clear mental clutter, widen perspective and reconnect with the answers you already hold.",
    links: [
      { label: "Tarot for business", url: "https://9ambusiness.co.uk/exclusive/why-tarot-reading-for-business-is-the-next-big-thing/" },
    ],
  },
  {
    number: "06",
    title: "Mental clutter & AI dependence",
    text: "Notice reassurance loops, information overload and outsourced thinking, then rebuild focus, discernment and trust in your own judgement.",
    links: [
      { label: "Is AI making entrepreneurs anxious?", url: "https://9ambusiness.co.uk/people-and-policy/hr/is-chat-ai-making-entrepreneurs-more-anxious-meet-the-uk-expert-warning-of-a-new-mental-trap-caused-by-ai-addiction/" },
    ],
  },
  {
    number: "07",
    title: "Relationships & projection",
    text: "Distinguish genuine connection from imagined potential, recognise repeated relationship patterns and stay present as trust develops.",
    links: [
      { label: "Fantasy bonds", url: "https://www.tyla.com/sex-and-relationships/fantasy-bond-relationships-meaning-examples-psychology-337325-20260722" },
      { label: "Age-gap relationships", url: "https://www.dailymail.com/lifestyle/article-16135525/Shirley-Bass-dance-student-expert-age-gap-relationships.html" },
    ],
  },
  {
    number: "08",
    title: "Signals beneath behaviour",
    text: "Become more aware of tension, withdrawal and emotional containment while avoiding simplistic or overconfident interpretations of others.",
    links: [
      { label: "Body language under pressure", url: "https://www.themirror.com/entertainment/bryan-kohberger-body-language-court-1966143" },
    ],
  },
  {
    number: "09",
    title: "Rest, perspective & renewal",
    text: "Create restorative space away from normal routines so the nervous system can settle and a different perspective can emerge.",
    links: [
      { label: "Tarot for mindfulness", url: "https://tarot.thought-reader.co.uk/" },
    ],
  },
];

type FormStatus = "idle" | "sending" | "sent" | "error";

function RetreatSite(): React.ReactElement {
  const [menuOpen, setMenuOpen] = useState(false);
  const [formStatus, setFormStatus] = useState<FormStatus>("idle");

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;

    if (!publicKey || !serviceId || !templateId) {
      setFormStatus("error");
      return;
    }

    setFormStatus("sending");
    try {
      await emailjs.send(
        serviceId,
        templateId,
        {
          to_email: "retreats@thought-reader.co.uk",
          from_name: formData.get("name"),
          from_email: formData.get("email"),
          phone: formData.get("phone"),
          retreat_type: formData.get("retreatType"),
          message: formData.get("message"),
        },
        { publicKey }
      );
      form.reset();
      setFormStatus("sent");
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="site-shell">
      <header className="site-header">
        <a className="brand" href="#top" aria-label="ThoughtLeader Retreats home">
          <span className="brand-mark">TR</span>
          <span><strong>ThoughtLeader</strong><small>RETREATS</small></span>
        </a>
        <button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Open navigation"><Menu /></button>
        <nav className={menuOpen ? "nav nav-open" : "nav"}>
          <a href="#retreats" onClick={() => setMenuOpen(false)}>Who we work with</a>
          <a href="#method" onClick={() => setMenuOpen(false)}>The method</a>
          <a href="#talking-points" onClick={() => setMenuOpen(false)}>Talking points</a>
          <a href="#about" onClick={() => setMenuOpen(false)}>About Phil</a>
          <button className="nav-cta" onClick={scrollToContact}>Plan a retreat <East /></button>
        </nav>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy reveal">
            <p className="eyebrow">ThoughtLeader retreats</p>
            <h1>We run retreats for people ready to <em>think differently.</em></h1>
            <p className="hero-intro">
              Tailored experiences for leadership teams, organisations, private groups and individuals,
              built around the practical, person-centred Thought Reader™ Method.
            </p>
            <div className="hero-actions">
              <button className="primary-button" onClick={scrollToContact}>Plan your retreat <ArrowForward /></button>
              <a href="#retreats" className="text-link">See who we work with <span>↓</span></a>
            </div>
          </div>
          <div className="hero-note"><span>Private groups</span><span>Leadership teams</span><span>Individuals</span></div>
        </section>

        <section className="statement section-pad">
          <p className="eyebrow">Space changes things</p>
          <div className="statement-grid">
            <h2>Step away from the noise. Come back with clarity.</h2>
            <div>
              <p>A retreat should not give you more to carry. It should create enough space to notice what matters, loosen what is stuck and choose what comes next.</p>
              <p>We shape every experience around the people in the room, the pressures they face and the change they want to create.</p>
            </div>
          </div>
        </section>

        <section id="retreats" className="audiences section-pad">
          <div className="section-heading split-heading">
            <div><p className="eyebrow light">Retreats for...</p><h2>Different people.<br />One considered approach.</h2></div>
            <p>From a focused one-to-one immersion to a full leadership offsite, format follows purpose, never a fixed package.</p>
          </div>
          <div className="audience-grid">
            {audiences.map((audience) => (
              <article className="audience-card" key={audience.title}>
                <span>{audience.number}</span><h3>{audience.title}</h3><p>{audience.text}</p><ArrowForward />
              </article>
            ))}
          </div>
        </section>

        <section id="method" className="method section-pad">
          <div className="method-intro">
            <p className="eyebrow">The Thought Reader™ Method</p>
            <h2>Powerful change without making the process another battle.</h2>
            <p>Phil's approach combines hypnotherapy, NLP, mindfulness, coaching and psychological techniques, tailored to the goals, pace and situation of each group.</p>
          </div>
          <div className="pillar-grid">
            {pillars.map((pillar, index) => (
              <article className="pillar" key={pillar.title}>
                <div className="pillar-top"><span>0{index + 1}</span>{pillar.icon}</div>
                <h3>{pillar.title}</h3><p>{pillar.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="talking-points" className="talking-points section-pad">
          <div className="section-heading split-heading talking-heading">
            <div><p className="eyebrow">Conversations worth making space for</p><h2>What we can explore together.</h2></div>
            <p>These are not off-the-shelf modules. They are starting points shaped by Phil's published work, two decades of client practice and what the people in the room actually need.</p>
          </div>
          <div className="talking-grid">
            {talkingPoints.map((point) => (
              <article className="talking-card" key={point.title}>
                <span className="talking-number">{point.number}</span>
                <h3>{point.title}</h3>
                <p>{point.text}</p>
                <div className="source-links">
                  {point.links.map((link) => (
                    <a href={link.url} target="_blank" rel="noreferrer" key={link.url}>{link.label}<ArrowForward /></a>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="experience section-pad">
          <div className="experience-image" role="img" aria-label="Peaceful countryside retreat house" />
          <div className="experience-copy">
            <p className="eyebrow">What we create</p>
            <h2>A retreat designed around your outcome.</h2>
            <p className="lead">A considered rhythm of guided work, restorative pauses, shared conversation and private reflection.</p>
            <div className="fit-list">
              {retreatFor.map((item) => <p key={item}><Check />{item}</p>)}
            </div>
            <p>Sessions can draw on mindset coaching, hypnotherapy, mindful movement, relaxation and tarot as a reflective tool for better questions, never fortune telling.</p>
          </div>
        </section>

        <section className="outcomes-ticker" aria-label="Areas our retreats cover">
          <div className="ticker-track">
            {[false, true].map((duplicate) => (
              <div className="ticker-group" aria-hidden={duplicate} key={String(duplicate)}>
                {outcomes.map((outcome) => (
                  <span key={outcome}>{outcome}<i aria-hidden="true" /></span>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section id="about" className="about section-pad">
          <div className="about-image" role="img" aria-label="Phil Macleod, founder of Thought Reader" />
          <div className="about-copy">
            <p className="eyebrow light">Your facilitator</p><h2>Phil Macleod</h2>
            <p className="role">Founder, Thought Leader | Thought Reader</p>
            <p>Phil is a UK-based clinical hypnotherapist and mindset practitioner with over 20 years' experience helping people create meaningful, lasting change.</p>
            <p>His style is conversational, practical and grounded in the belief that people under pressure do not need more complexity. They need useful techniques they can understand, practise and take away.</p>
            <blockquote>“Change does not have to be painful to be powerful.”</blockquote>
          </div>
        </section>

        <section id="contact" className="contact section-pad">
          <div className="contact-intro">
            <p className="eyebrow">Start a conversation</p><h2>Who is your retreat for?</h2>
            <p>You do not need the perfect brief. Tell Phil about the people, the context and what you would like to be different. We will shape the right experience from there.</p>
            <div className="contact-points"><span>Confidential conversation</span><span>UK & international</span><span>No-pressure first chat</span></div>
          </div>
          <form className="contact-form" onSubmit={handleSubmit}>
            <label>NAME<input name="name" required autoComplete="name" /></label>
            <label>EMAIL<input name="email" type="email" required autoComplete="email" /></label>
            <label>PHONE <small>OPTIONAL</small><input name="phone" type="tel" autoComplete="tel" /></label>
            <label>RETREAT FOR<select name="retreatType" defaultValue=""><option value="" disabled>Select one</option><option>Leadership team</option><option>Organisation</option><option>Private group</option><option>Individual</option><option>Not sure yet</option></select></label>
            <label className="full-field">WHAT WOULD YOU LIKE THE RETREAT TO ACHIEVE?<textarea name="message" required rows={4} /></label>
            <button className="submit-button" type="submit" disabled={formStatus === "sending"}>{formStatus === "sending" ? "Sending..." : "Send a confidential enquiry"}<ArrowForward /></button>
            {formStatus === "sent" && <p className="form-message success">Thank you. Phil will be in touch shortly.</p>}
            {formStatus === "error" && <p className="form-message error">The message could not be sent. Please try again or email retreats@thought-reader.co.uk.</p>}
          </form>
        </section>
      </main>

      <footer>
        <a className="brand footer-brand" href="#top"><span className="brand-mark">TR</span><span><strong>ThoughtLeader</strong><small>RETREATS</small></span></a>
        <p>Practical, reflective retreat experiences for meaningful change.</p>
        <div><a href="mailto:retreats@thought-reader.co.uk">retreats@thought-reader.co.uk</a><span>© 2026 Thought Leader | Thought Reader</span></div>
      </footer>
    </div>
  );
}

export default RetreatSite;